import { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogDescription } from "./ui/Dialog";
import { Button } from "./ui/Button";
import { Input, Textarea, Label } from "./ui/Input";
import { useToast } from "./ui/Toast";
import { useSDK } from "../lib/sdk-context";
import { api } from "../lib/api";
import type { BucketRow } from "./BucketList";

export default function DeliveryPartnerModal({
  row,
  open,
  onOpenChange,
  onSaved,
}: {
  row: BucketRow | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSaved: () => void;
}) {
  const { context } = useSDK();
  const toast = useToast();
  const [displayName, setDisplayName] = useState("");
  const [website, setWebsite] = useState("");
  const [serviceAreaText, setServiceAreaText] = useState("");
  const [cityInput, setCityInput] = useState("");
  const [cities, setCities] = useState<string[]>([]);
  const [licenseNumber, setLicenseNumber] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (row && open) {
      setDisplayName(row.name || row.parent_name || "");
      setWebsite(row.website || row.brand_supplied_website || "");
      setServiceAreaText("");
      setCities([]);
      setCityInput("");
      setLicenseNumber(row.cm_license || row.license_number || "");
    }
  }, [row, open]);

  const addCity = () => {
    const c = cityInput.trim();
    if (!c) return;
    if (!cities.includes(c)) setCities([...cities, c]);
    setCityInput("");
  };

  const onSubmit = async () => {
    if (!row) return;
    if (!displayName.trim()) { toast.push("Display name is required", "error"); return; }
    setSaving(true);
    try {
      await api.post(context, "/api/delivery-partner", {
        display_name: displayName.trim(),
        website: website.trim() || null,
        service_area_text: serviceAreaText.trim() || null,
        service_area_cities: cities.length ? cities : null,
        license_number: licenseNumber.trim() || null,
        source_row_id: row.source_row_id,
      });
      toast.push("Saved as Delivery Partner. Edit anytime in your partner list.");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.push(`Save failed: ${(err as Error).message}`, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTitle>Mark as Delivery Partner</DialogTitle>
      <DialogDescription>
        Save this retailer as delivery-only. They'll appear on your Retailers pages and be eligible for deals, with a service area instead of a street address.
      </DialogDescription>

      <div className="mt-5 space-y-4">
        <div>
          <Label htmlFor="dp-name">Display name</Label>
          <Input id="dp-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="e.g. Grassdoor" />
        </div>
        <div>
          <Label htmlFor="dp-web">Website</Label>
          <Input id="dp-web" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="grassdoor.com" />
        </div>
        <div>
          <Label htmlFor="dp-area">Service area</Label>
          <Textarea id="dp-area" value={serviceAreaText} onChange={(e) => setServiceAreaText(e.target.value)} placeholder="Greater LA, Orange County" rows={2} />
        </div>
        <div>
          <Label htmlFor="dp-city" hint="(optional — press Enter or click Add)">Cities served</Label>
          <div className="flex gap-2">
            <Input
              id="dp-city"
              value={cityInput}
              onChange={(e) => setCityInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCity(); } }}
              placeholder="Los Angeles"
            />
            <Button variant="secondary" type="button" onClick={addCity}>Add</Button>
          </div>
          {cities.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {cities.map((c) => (
                <span key={c} className="inline-flex items-center gap-1 rounded-full bg-accent-50 text-accent-700 border border-accent-100 px-2.5 py-0.5 text-xs">
                  {c}
                  <button type="button" onClick={() => setCities(cities.filter((x) => x !== c))} className="text-accent-700/70 hover:text-accent-700">×</button>
                </span>
              ))}
            </div>
          )}
        </div>
        <div>
          <Label htmlFor="dp-lic" hint="(optional)">License number</Label>
          <Input id="dp-lic" value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} placeholder="C9-0000000-LIC" />
          <p className="mt-1 text-xs text-gray-500">Helpful for compliance even if we're not using it for rendering.</p>
        </div>
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={onSubmit} disabled={saving}>
          {saving ? "Saving…" : "Save as Delivery Partner"}
        </Button>
      </div>
    </Dialog>
  );
}
